// Pokeriautomaatti (user request 2026-10-03): the Finnish RAY-style joker poker machine with doubling.
// Pure rules shared by the server (which shuffles and pays) and the browser (which shows the hands).
//
// Cards 0–51 as in the other card games (rank 2 + c % 13, ace 14; suit ⌊c / 13⌋: ♠ ♥ ♦ ♣), 52 is the joker.
// The joker stands for any card. The lowest win is two pairs. After a win the player may double:
// guess whether the next card from a fresh 53-card deck is small (A–6) or big (8–K). The joker always
// wins, a red seven gives the stake of that doubling back and ends it, a black seven loses.

export type Card = number;
export const JOKER = 52;
export const DECK_SIZE = 53;
export const HAND_SIZE = 5;
export const rankOf = (c: Card) => 2 + (c % 13);
export const suitOf = (c: Card) => Math.floor(c / 13);
export const isRed = (c: Card) => c !== JOKER && (suitOf(c) === 1 || suitOf(c) === 2);

/** Bets per hand in AreCoin (as the 20 cent – 1 euro of the real machine). */
export const POKER_BETS = [1, 2, 3, 4, 5] as const;
/** The top prize is this many times the bet; a win may be doubled while at most half of it. */
export const TOP_MULTIPLIER = 50;

export type PokerHand = 'five' | 'straight_flush' | 'four' | 'full_house' | 'flush' | 'straight' | 'three' | 'two_pairs';
/** Best first. */
export const POKER_HANDS: readonly PokerHand[] = ['five', 'straight_flush', 'four', 'full_house', 'flush', 'straight', 'three', 'two_pairs'];

/**
 * Wins × the bet. Chosen by simulation (poker.test.ts) so that a sensible player gets back about 96–97 %
 * (the doubling is 98.1 %); the top prizes are 50 × the bet like the real machine's 50 euros.
 */
export const PAYTABLE: Record<PokerHand, number> = {
  five: 50,
  straight_flush: 50,
  four: 20,
  full_house: 8,
  flush: 5,
  straight: 4,
  three: 2,
  two_pairs: 2,
};

/** A shuffled deck of 53 cards. `random(n)` returns an integer 0 … n−1. */
export function shuffled(random: (n: number) => number): Card[] {
  const deck = Array.from({ length: DECK_SIZE }, (_, i) => i);
  for (let i = deck.length - 1; i > 0; i--) {
    const j = random(i + 1);
    [deck[i], deck[j]] = [deck[j]!, deck[i]!];
  }
  return deck;
}

function naturalHand(cards: Card[]): PokerHand | null {
  const ranks = cards.map(rankOf).sort((a, b) => a - b);
  const flush = cards.every((c) => suitOf(c) === suitOf(cards[0]!));
  const unique = new Set(ranks).size === 5;
  // The ace counts as 1 or 14, whichever is better.
  const straight = unique && (ranks[4]! - ranks[0]! === 4 || ranks.join() === '2,3,4,5,14');
  if (straight && flush) return 'straight_flush';
  const counts = new Map<number, number>();
  for (const r of ranks) counts.set(r, (counts.get(r) ?? 0) + 1);
  const groups = [...counts.values()].sort((a, b) => b - a);
  if (groups[0] === 4) return 'four';
  if (groups[0] === 3 && groups[1] === 2) return 'full_house';
  if (flush) return 'flush';
  if (straight) return 'straight';
  if (groups[0] === 3) return 'three';
  if (groups[0] === 2 && groups[1] === 2) return 'two_pairs';
  return null;
}

const better = (a: PokerHand | null, b: PokerHand | null) => (a === null ? b : b === null ? a : POKER_HANDS.indexOf(a) <= POKER_HANDS.indexOf(b) ? a : b);

/** The best hand of five cards; the joker becomes whatever card is best. */
export function evaluate(cards: readonly Card[]): PokerHand | null {
  const j = cards.indexOf(JOKER);
  if (j < 0) return naturalHand([...cards]);
  const rest = cards.filter((c) => c !== JOKER);
  // Five of a kind: four of the same rank and the joker.
  if (rest.every((c) => rankOf(c) === rankOf(rest[0]!))) return 'five';
  let best: PokerHand | null = null;
  for (let c = 0; c < 52; c++) {
    best = better(best, naturalHand([...rest, c]));
    if (best === 'straight_flush') break;
  }
  return best;
}

export const payout = (hand: PokerHand | null, bet: number) => (hand ? PAYTABLE[hand] * bet : 0);

export type DoubleGuess = 'small' | 'big';
export type DoubleResult = 'win' | 'lose' | 'push';

/** The doubling card: the joker wins, a red seven gives the stake back, a black seven loses; ace = 1. */
export function doubleResult(card: Card, guess: DoubleGuess): DoubleResult {
  if (card === JOKER) return 'win';
  const r = rankOf(card) === 14 ? 1 : rankOf(card);
  if (r === 7) return isRed(card) ? 'push' : 'lose';
  return (r < 7) === (guess === 'small') ? 'win' : 'lose';
}

/** May this win be doubled (at most half of the top prize)? */
export const canDouble = (win: number, bet: number) => win > 0 && win <= (TOP_MULTIPLIER * bet) / 2;

/**
 * A sensible hold (used by the tests to check the payout, and as the "hint" of nothing else): keeps made
 * hands, three of a kind, two pairs, four to a straight flush or flush, a pair, four to an open straight,
 * the joker. Returns the indexes to keep.
 */
export function sensibleHold(cards: readonly Card[]): number[] {
  const all = [0, 1, 2, 3, 4];
  const made = evaluate(cards);
  if (made && POKER_HANDS.indexOf(made) <= POKER_HANDS.indexOf('straight')) return all;
  const joker = cards.indexOf(JOKER);
  const naturals = all.filter((i) => i !== joker);
  const byRank = new Map<number, number[]>();
  for (const i of naturals) byRank.set(rankOf(cards[i]!), [...(byRank.get(rankOf(cards[i]!)) ?? []), i]);
  const groups = [...byRank.values()].sort((a, b) => b.length - a.length);
  const withJoker = (idx: number[]) => (joker >= 0 ? [...idx, joker] : idx);
  if (made === 'four') return all;
  if (made === 'three') return withJoker(groups[0]!);
  if (made === 'two_pairs') return groups.filter((g) => g.length === 2).flat();
  // Four to a straight flush or a flush (the joker counts).
  for (let s = 0; s < 4; s++) {
    const same = naturals.filter((i) => suitOf(cards[i]!) === s);
    if (same.length + (joker >= 0 ? 1 : 0) >= 4) return withJoker(same);
  }
  const pair = groups.find((g) => g.length === 2);
  if (pair) return withJoker(pair);
  if (joker >= 0) return [joker];
  // Four to an open straight.
  const ranks = naturals.map((i) => rankOf(cards[i]!));
  for (let low = 2; low <= 10; low++) {
    const run = [low, low + 1, low + 2, low + 3];
    if (run.every((r) => ranks.includes(r))) return run.map((r) => naturals.find((i) => rankOf(cards[i]!) === r)!);
  }
  return [];
}
